import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Typography,
    Grid,
} from '@mui/material';
import { useUpdateAiDraft, useListArticleFilters } from '../services/appService';

function AiDraftEditModal({ open, onClose, onSave, draft, appId }) {
    const [formData, setFormData] = useState({ title: '', body_md: '', draft_type: '', image_url: '' });
    const [filters, setFilters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterLoading, setFilterLoading] = useState(false);
    const [error, setError] = useState('');

    const updateDraftHook = useUpdateAiDraft();
    const listFiltersHook = useListArticleFilters(); // Use the existing hook

    useEffect(() => {
        if (open && draft) {
            console.log('[AiDraftEditModal] Opening for draft:', draft);
            setFormData({
                title: draft.title || '',
                body_md: draft.body_md || '',
                draft_type: draft.draft_type || '', // Use draft_type from the draft
                image_url: draft.image_url || '',
            });
            setError('');

            // Fetch filters for the category dropdown
            setFilterLoading(true);
            listFiltersHook(appId)
                .then(filterData => {
                    setFilters(filterData || []);
                    // Set default if draft_type is not in fetched filters?
                    const draftCategoryExists = (filterData || []).some(f => f.name === draft.draft_type);
                    if (!draftCategoryExists && (filterData || []).length > 0) {
                        console.warn(`Draft category "${draft.draft_type}" not found in filters. Resetting to first available.`);
                        // Optionally reset to the first available filter if the current one is invalid
                        // setFormData(prev => ({ ...prev, draft_type: filterData[0].name }));
                    }
                })
                .catch(filterErr => {
                    console.error("Failed to fetch article filters:", filterErr);
                    setError('Failed to load categories.');
                    setFilters([]);
                })
                .finally(() => setFilterLoading(false));

        } else if (!open) {
            // Reset form when modal is closed
            setFormData({ title: '', body_md: '', draft_type: '', image_url: '' });
            setFilters([]);
            setError('');
        }
    }, [open, draft, appId, listFiltersHook]);

    const handleChange = (event) => {
        setFormData(prev => ({
            ...prev,
            [event.target.name]: event.target.value
        }));
    };

    const handleSave = async () => {
        if (!draft || !draft.id) return;
        setLoading(true);
        setError('');
        try {
            await updateDraftHook(appId, draft.id, formData);
            onSave(); // Trigger refresh in parent modal
            onClose(); // Close this modal
        } catch (err) {
            console.error("Error updating draft:", err);
            setError(err.detail || err.message || 'Failed to save changes.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Edit AI Draft (ID: {draft?.id})</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                <Box component="form" noValidate sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                margin="dense"
                                required
                                fullWidth
                                id="title"
                                label="Title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                disabled={loading}
                                autoFocus
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="dense" required disabled={loading || filterLoading}>
                                <InputLabel id="draft-type-label">Category</InputLabel>
                                <Select
                                    labelId="draft-type-label"
                                    id="draft_type"
                                    name="draft_type"
                                    value={formData.draft_type}
                                    label="Category"
                                    onChange={handleChange}
                                >
                                    {filters.length === 0 && !filterLoading && <MenuItem value="" disabled>No categories found</MenuItem>}
                                    {filterLoading && <MenuItem value="" disabled>Loading categories...</MenuItem>}
                                    {filters.map((filt) => (
                                        <MenuItem key={filt.id || filt.name} value={filt.name}>
                                            {filt.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                fullWidth
                                id="image_url"
                                label="Image URL (Optional)"
                                name="image_url"
                                value={formData.image_url}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={12}>
                             <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>Summary/Body (Markdown)</Typography>
                             <TextField
                                fullWidth
                                multiline
                                rows={10}
                                variant="outlined"
                                name="body_md"
                                value={formData.body_md}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ pb: 2, px: 3 }}>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AiDraftEditModal; 